import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Row, Col, Form, Input, Button, Checkbox, message } from "antd";
import { UserOutlined, LockOutlined } from "@ant-design/icons";
import { useAuth } from "../../context/AuthContext";
import { AxiosHelper } from "../../utils/helpers/axios.helper";
import { ReactComponent as DropIcon } from "../../assets/icons/drop.svg";
import "./login.scss";
import { LoginForm, LoginResponse } from "../../interfaces/auth.interface";
import { Typography } from "antd";
const { Text, Title } = Typography;

const Login: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { login } = useAuth();

    const validateEmail = (rule: any, value: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!value) {
            return Promise.reject(new Error("Please input your username/email!"));
        }

        if (!value.includes("@")) {
            return Promise.reject(new Error("Please enter a valid email address"));
        }

        if (!emailRegex.test(value)) {
            return Promise.reject(new Error("Invalid email format"));
        }

        return Promise.resolve();
    };

    const onFinish = async (values: LoginForm) => {
        setLoading(true);
        try {
            const { data } = await AxiosHelper.instance.post<LoginResponse>(
                "/api/admin-auth/login",
                {
                    email: values.email,
                    password: values.password,
                }
            );
            login(data, values.remember);
            navigate("/dashboard");
        } catch (error: any) {
            message.error(error?.response?.data?.message || "Login failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Row className="page-container">
            <Col className="login-page">
                <Row className="login-row">
                    <Col xs={0} sm={0} md={12} lg={12} xl={12} className="login-left-col">
                        <Row className="ocean-bg" />
                    </Col>
                    <Col
                        xs={24}
                        sm={24}
                        md={12}
                        lg={12}
                        xl={12}
                        className="login-right-col"
                    >
                        <Row className="login-form-container">
                            <Col className="login-header">
                                <Row className="title-group">
                                    <DropIcon className="drop-icon" />
                                    <Title level={2}>Welcome back, Emre!</Title>
                                </Row>
                                <Row justify={"center"}>
                                    <Text>Sign in to your account to continue</Text>
                                </Row>
                            </Col>

                            <Form
                                name="login"
                                initialValues={{ remember: true }}
                                onFinish={onFinish}
                                layout="vertical"
                                size="large"
                                className="login-form"
                            >
                                <Form.Item name="email" rules={[{ validator: validateEmail }]}>
                                    <Input
                                        prefix={<UserOutlined className="input-icon" />}
                                        placeholder="Username"
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="password"
                                    rules={[
                                        { required: true, message: "Please input your password!" },
                                    ]}
                                >
                                    <Input.Password
                                        prefix={<LockOutlined className="input-icon" />}
                                        placeholder="Password"
                                    />
                                </Form.Item>

                                <Row
                                    style={{ paddingBottom: 24 }}
                                    justify="space-between"
                                    align="middle"
                                >
                                    <Col>
                                        <Form.Item name="remember" valuePropName="checked" noStyle>
                                            <Checkbox>Keep me logged in</Checkbox>
                                        </Form.Item>
                                    </Col>
                                    <Col>
                                        <Link to="/info">
                                            <Text className="forgot-password-text">
                                                Forgot password?
                                            </Text>
                                        </Link>
                                    </Col>
                                </Row>

                                <Form.Item>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        loading={loading}
                                        block
                                        className="login-button"
                                    >
                                        Log in
                                    </Button>
                                </Form.Item>

                                <Col className="info">
                                    <Link className="info-text" to="/info">
                                        Don't have an account?
                                    </Link>
                                </Col>
                            </Form>
                        </Row>
                    </Col>
                </Row>
            </Col>
        </Row>
    );
};

export default Login;
